import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import { ProcesVerbalForm, ProcesVerbalHeader, ProcesVerbalList, ProcesVerbalDetails } from './proces';

const ProcesVerbauxTab = ({ onDataChange }) => {
  // États de base
  const [procesVerbaux, setProcesVerbaux] = useState([]);
  const [soutenances, setSoutenances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    soutenance: '',
    mention: '',
    decision: '',
    valide: ''
  });

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPV, setSelectedPV] = useState(null);
  const [detailPV, setDetailPV] = useState(null);
  const [formData, setFormData] = useState({
    soutenance: '',
    note_finale: '',
    mention: 'PASSABLE',
    decision: 'ADMIS',
    observations: '',
    recommandations: '',
    date_signature_president: '',
    date_signature_rapporteur: '',
    date_signature_examinateur: '',
    valide: false
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pvToDelete, setPvToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // États pour les statistiques
  const [stats, setStats] = useState(null);

  // Récupération des données
  const fetchProcesVerbaux = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      // Ajouter les filtres
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      const response = await api.get(`/soutenance/proces-verbaux/?${queryParams}`);
      
      if (response.data) {
        let responseData = [];
        let paginationData = null;
        
        if (response.data.data) {
          responseData = response.data.data;
          paginationData = response.data.pagination;
        } else if (response.data.results) {
          responseData = response.data.results;
          paginationData = {
            count: response.data.count || 0,
            total_pages: Math.ceil((response.data.count || 0) / pageSize),
            current_page: currentPage,
            page_size: pageSize,
            has_next: !!response.data.next,
            has_previous: !!response.data.previous
          };
        } else {
          responseData = Array.isArray(response.data) ? response.data : [];
        }
        
        setProcesVerbaux(Array.isArray(responseData) ? responseData : []);
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des procès-verbaux');
      setProcesVerbaux([]);
      setPagination(null);
      toast.error('Impossible de charger les procès-verbaux');
    } finally {
      setLoading(false);
    }
  };

  const fetchSoutenances = async () => {
    try {
      const response = await api.get('/soutenance/soutenances/');
      if (response.data?.data) {
        setSoutenances(response.data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des soutenances:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/soutenance/proces-verbaux/stats/');
      if (response.data?.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProcesVerbaux();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm, filters]);

  useEffect(() => {
    fetchSoutenances();
    fetchStats();
  }, []);

  // Gestion de la pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal pour créer un nouveau PV seulement
  const handleOpenModal = () => {
    setSelectedPV(null);
    setFormData({
      soutenance: '',
      note_finale: '',
      mention: 'PASSABLE',
      decision: 'ADMIS',
      observations: '',
      recommandations: '',
      date_signature_president: '',
      date_signature_rapporteur: '',
      date_signature_examinateur: '',
      valide: false
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de génération rapide
  const handleQuickCreate = (soutenance) => {
    setSelectedPV(null);
    setFormData({
      soutenance: soutenance.id,
      note_finale: '',
      mention: 'PASSABLE',
      decision: 'ADMIS',
      observations: '',
      recommandations: '',
      date_signature_president: '',
      date_signature_rapporteur: '',
      date_signature_examinateur: '',
      valide: false
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (pv) => {
    setDetailPV(pv);
    setIsDetailModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.soutenance) {
      errors.soutenance = "La soutenance est requise";
    } else {
      // Vérifier si la soutenance existe
      const selectedSoutenance = soutenances.find(s => s.id === parseInt(formData.soutenance));
      
      if (selectedSoutenance) {
        // Vérifier si la date de soutenance est passée
        const soutenanceDate = new Date(selectedSoutenance.date_soutenance);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Réinitialiser l'heure pour comparer seulement les dates
        
        if (soutenanceDate > today) {
          errors.soutenance = "Impossible de créer un procès-verbal pour une soutenance dont la date n'est pas encore arrivée";
        }
        
        // Vérifier si cette soutenance a déjà un procès-verbal
        const existingPV = procesVerbaux.find(pv => pv.soutenance?.id === selectedSoutenance.id);
        if (existingPV) {
          errors.soutenance = "Cette soutenance a déjà un procès-verbal";
        }
      }
    }
    
    if (!formData.note_finale || formData.note_finale < 0 || formData.note_finale > 20) {
      errors.note_finale = "La note finale est requise (0-20)";
    }
    if (!formData.mention) {
      errors.mention = "La mention est requise";
    }
    if (!formData.decision) {
      errors.decision = "La décision est requise";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire (création seulement)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        note_finale: parseFloat(formData.note_finale)
      };

      await api.post('/soutenance/proces-verbaux/', dataToSend);
      toast.success("Procès-verbal créé avec succès");

      fetchProcesVerbaux();
      fetchStats();
      setIsModalOpen(false);
      onDataChange && onDataChange();
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
        errorMsg = "Veuillez corriger les erreurs dans le formulaire.";
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (pv) => {
    setPvToDelete(pv);
    setConfirmConfig({
      title: "Supprimer le procès-verbal",
      message: `Êtes-vous sûr de vouloir supprimer le procès-verbal de la soutenance "${getSoutenanceLabel(pv.soutenance)}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pvToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/soutenance/proces-verbaux/${pvToDelete.id}/`);
      toast.success("Procès-verbal supprimé avec succès");
      fetchProcesVerbaux();
      fetchStats();
      onDataChange && onDataChange();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer ce procès-verbal. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Validation d'un PV
  const handleValidate = async (pv) => {
    if (window.confirm('Êtes-vous sûr de vouloir valider définitivement ce procès-verbal ?')) {
      try {
        await api.post(`/soutenance/proces-verbaux/${pv.id}/valider/`);
        toast.success("Procès-verbal validé avec succès");
        fetchProcesVerbaux();
        fetchStats();
        onDataChange && onDataChange();
      } catch (error) {
        console.error("Erreur lors de la validation:", error);
        toast.error("Impossible de valider ce procès-verbal.");
      }
    }
  };

  // Fonction utilitaire
  const getSoutenanceLabel = (soutenance) => {
    if (!soutenance) return 'Non définie';
    
    let etudiantsNoms = '';
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiant = soutenance.etudiants_soutenance[0].etudiant;
      etudiantsNoms = `${etudiant.nom} ${etudiant.prenom}`;
    } else if (soutenance.etudiant_principal_info) {
      etudiantsNoms = `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    return etudiantsNoms || soutenance.titre_travail?.substring(0, 40) || 'Soutenance';
  };

  // Soutenances sans PV
  const soutenancesSansPV = soutenances.filter(soutenance => 
    !procesVerbaux.some(pv => pv.soutenance?.id === soutenance.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <ProcesVerbalHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => handleOpenModal()}
        filters={filters}
        onFilterChange={setFilters}
        soutenances={soutenances}
        soutenancesSansPV={soutenancesSansPV}
        onQuickCreate={handleQuickCreate}
        stats={stats}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Liste des procès-verbaux */}
      <ProcesVerbalList
        procesVerbaux={procesVerbaux}
        loading={loading}
        onView={handleOpenDetailModal}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Modal de création */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nouveau procès-verbal"
        maxWidth="max-w-4xl"
        footer={
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => setIsModalOpen(false)}
            >
              Annuler
            </button>
            <button
              type="button"
              className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </>
              ) : (
                'Créer'
              )}
            </button>
          </div>
        }
      >
        <ProcesVerbalForm
          formData={formData}
          formErrors={formErrors}
          selectedPV={null}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          soutenances={soutenances}
          procesVerbaux={procesVerbaux}
        />
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails du procès-verbal"
        maxWidth="max-w-4xl"
      >
        <ProcesVerbalDetails procesVerbal={detailPV} />
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le procès-verbal"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce procès-verbal ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default ProcesVerbauxTab;

